// Catalog and voice-usage settlement for Speak billing.
// Deno-free so Vitest can import it. This module is the in-repo price table.
// Stripe product prices are checked against it at checkout and are not edited here.
//
// Customer-facing plans (Billing page / user guide):
//   Pay as you go  £12.21/hour, no included call credit, 3 lines, extra lines £25/month
//   Pro            £999/month with £499 call credit, or $1,499/month with $750 call credit
//   Managed Campaign  £1,000 or $1,500 one-off, up to 1,000 contacts, not metered per second
//   Pay as you go  £12.21/hour or $15.50/hour
//   Packs          archived; checkout must not use those product ids
// Overage past included call credit (account currency):
//   UK £12.21/hour, US $15.50/hour, billed per second
// Compliance Plus (Mneme Cloud) is a GBP usage rate, not an add-on on Pro or Pay as you go:
//   £16.50/hour including talk time, shown as "£16.50/hr + VAT"
//   £16.50 is exclusive of VAT. This module does not calculate or add VAT.
//   Per second, same ceil as Pay as you go: ceil(seconds × 1650 / 3600).
//   Those seconds are not also billed at the £12.21 talk-time rate.
//   No USD Compliance Plus rate is confirmed.
// VAT is not collected in checkout.
// Credit counts are an internal ledger. Screens convert them with the helpers below.

import type { AccountCurrency } from "./accountCurrency.ts";

export const STANDARD_VOICE_CREDITS_PER_MINUTE = 1.75;
export const LEGACY_CREDITS_PER_POUND = 67;
export const ROLLOVER_CAP_RATIO = 0.5;
export const UK_OVERAGE_PENCE_PER_HOUR = 1221;
export const US_OVERAGE_CENTS_PER_HOUR = 1550;
export const PRO_PRICE_PENCE = 99900;
export const PRO_PRICE_CENTS = 149900;
export const PRO_INCLUDED_CREDIT_PENCE = 49900;
export const PRO_INCLUDED_CREDIT_CENTS = 75000;
export const MANAGED_CAMPAIGN_PENCE = 100000;
export const MANAGED_CAMPAIGN_CENTS = 150000;
export const PAYG_CENTS_PER_HOUR = US_OVERAGE_CENTS_PER_HOUR;
/** Compliance Plus (Mneme), pence per hour, talk time included. Exclusive of VAT. */
export const COMPLIANCE_PLUS_PENCE_PER_HOUR = 1650;
/** Customer-facing price label. Display only; no VAT is added to the amount. */
export const COMPLIANCE_PLUS_PRICE_LABEL = "£16.50/hr + VAT";
/**
 * Edge secret Accounting sets after Phillip approves the live Stripe price.
 * This is the variable name, not a Stripe price id. Leave it unset here.
 * Per-second unit amount for that price is 1650 / 3600 = 0.458333333333 pence.
 */
export const STRIPE_PRICE_COMPLIANCE_PLUS_GBP_ENV = "STRIPE_PRICE_COMPLIANCE_PLUS_GBP";

/** Live Stripe catalog. Checkout uses these ids. This file does not create or edit them. */
export const STRIPE_CATALOG = {
  pro: {
    productId: "prod_Syf1HHtdUwIHnr",
    gbp: { priceId: "price_1S2hhDHfjIU2jToWouAlo8hK", unitAmount: PRO_PRICE_PENCE, interval: "month" as const },
    usd: { priceId: "price_1UQbOTHfjIU2jToWznOWTfTP", unitAmount: PRO_PRICE_CENTS, interval: "month" as const },
  },
  managedCampaign: {
    productId: "prod_VRUNkFbLML2O6F",
    gbp: { priceId: "price_1UQbOVHfjIU2jToWM0gYBa5i", unitAmount: MANAGED_CAMPAIGN_PENCE, interval: "month" as const },
    usd: { priceId: "price_1UQbOWHfjIU2jToWIHCfiX2s", unitAmount: MANAGED_CAMPAIGN_CENTS, interval: "month" as const },
  },
  payAsYouGo: {
    productId: "prod_VRUNieZ54yk2SP",
    gbp: { priceId: "price_1UQbOXHfjIU2jToWXx9spTBe", unitAmount: UK_OVERAGE_PENCE_PER_HOUR, interval: "hour" as const },
    usd: { priceId: "price_1UQbOYHfjIU2jToWISlsa0hZ", unitAmount: PAYG_CENTS_PER_HOUR, interval: "hour" as const },
  },
  extraLine: {
    productId: "prod_TcmHaDMjWra62t",
    priceId: "price_1T1PoIHfjIU2jToWMkCX1VRm",
  },
} as const;

export const ARCHIVED_CREDIT_PACK_PRODUCT_IDS = [
  "prod_Syzps3mqvlfzob",
  "prod_SyzrqY2ndJphjO",
  "prod_Syzs2TwY8CRAZT",
] as const;

/**
 * Nearest minor units for an internal credit balance.
 * Same hourly rate as overage: credits × minorPerHour / (creditsPerMinute × 60).
 */
export function minorUnitsForCredits(
  credits: number,
  minorPerHour: number,
  creditsPerMinute = STANDARD_VOICE_CREDITS_PER_MINUTE,
): number {
  if (!Number.isFinite(credits) || credits <= 0) return 0;
  if (!Number.isFinite(minorPerHour) || minorPerHour <= 0) return 0;
  if (!Number.isFinite(creditsPerMinute) || creditsPerMinute <= 0) return 0;
  return Math.round((credits * minorPerHour) / (creditsPerMinute * 60));
}

/**
 * Integer credits worth at least `minor` at the hourly rate.
 * Ceil keeps a £499 allowance from landing short of £499 after rounding.
 */
export function creditsForMinorUnits(
  minor: number,
  minorPerHour: number,
  creditsPerMinute = STANDARD_VOICE_CREDITS_PER_MINUTE,
): number {
  if (!Number.isFinite(minor) || minor <= 0) return 0;
  if (!Number.isFinite(minorPerHour) || minorPerHour <= 0) return 0;
  if (!Number.isFinite(creditsPerMinute) || creditsPerMinute <= 0) return 0;
  return Math.ceil((minor * creditsPerMinute * 60) / minorPerHour);
}

export const PRICE_TABLE = {
  currency: "gbp" as const,
  vat: {
    collectedInCheckout: false,
    automaticTax: false,
  },
  overage: {
    uk: { currency: "gbp" as const, minorPerHour: UK_OVERAGE_PENCE_PER_HOUR },
    us: { currency: "usd" as const, minorPerHour: US_OVERAGE_CENTS_PER_HOUR },
  },
  compliancePlus: {
    implemented: true,
    productName: "Mneme",
    includesTalkTime: true,
    /** Exclusive of VAT. The label says "+ VAT"; checkout does not add it. */
    vat: "exclusive" as const,
    priceLabel: COMPLIANCE_PLUS_PRICE_LABEL,
    uk: { currency: "gbp" as const, minorPerHour: COMPLIANCE_PLUS_PENCE_PER_HOUR },
    /** No confirmed USD rate. Do not bill the retired $2/hour add-on. */
    us: { currency: "usd" as const, minorPerHour: null },
  },
  plans: {
    payAsYouGo: {
      tier: "pay as you go",
      unitAmountPence: 1221,
      interval: "hour" as const,
      includedCredits: 0,
      includedConcurrentCalls: 3,
      extraLinePencePerMonth: 2500,
    },
    pro: {
      tier: "pro",
      unitAmountPence: PRO_PRICE_PENCE,
      unitAmountCents: PRO_PRICE_CENTS,
      interval: "month" as const,
      includedCreditPence: PRO_INCLUDED_CREDIT_PENCE,
      includedCreditCents: PRO_INCLUDED_CREDIT_CENTS,
      includedCredits: creditsForMinorUnits(PRO_INCLUDED_CREDIT_PENCE, UK_OVERAGE_PENCE_PER_HOUR),
      includedCreditsUsd: creditsForMinorUnits(PRO_INCLUDED_CREDIT_CENTS, US_OVERAGE_CENTS_PER_HOUR),
      stripeProductId: STRIPE_CATALOG.pro.productId,
      includedConcurrentCalls: 10,
      extraLinePencePerMonth: 2000,
    },
    managedCampaign: {
      tier: "managed campaign",
      unitAmountPence: MANAGED_CAMPAIGN_PENCE,
      unitAmountCents: MANAGED_CAMPAIGN_CENTS,
      interval: "month" as const,
      stripeProductId: STRIPE_CATALOG.managedCampaign.productId,
    },
    starter: {
      tier: "starter",
      includedCredits: 1000,
      extraLinePencePerMonth: 2500,
    },
    growth: {
      tier: "growth",
      includedCredits: 5000,
      extraLinePencePerMonth: 2200,
    },
  },
  packs: {
    small: { credits: 850, unitAmountPence: 9900, productId: "prod_Syzps3mqvlfzob", interval: null },
    medium: { credits: 1750, unitAmountPence: 19900, productId: "prod_SyzrqY2ndJphjO", interval: null },
    large: { credits: 4500, unitAmountPence: 49900, productId: "prod_Syzs2TwY8CRAZT", interval: null },
  },
  voice: {
    creditsPerMinute: STANDARD_VOICE_CREDITS_PER_MINUTE,
    rateName: "standard_voice",
    billableStatuses: ["completed"] as const,
    legacyCreditsPerPound: LEGACY_CREDITS_PER_POUND,
  },
} as const;

export function minorPerHourForCurrency(currency: AccountCurrency): number {
  return currency === "usd" ? PRICE_TABLE.overage.us.minorPerHour : PRICE_TABLE.overage.uk.minorPerHour;
}

/** Same amount of calling time in the other currency, using the overage hourly rates. */
export function convertMinorByOverageRate(minor: number, from: AccountCurrency, to: AccountCurrency): number {
  if (!Number.isFinite(minor)) return 0;
  if (from === to) return Math.round(minor);
  const fromRate = minorPerHourForCurrency(from);
  const toRate = minorPerHourForCurrency(to);
  if (fromRate <= 0) return 0;
  return Math.round((minor * toRate) / fromRate);
}

export function formatMinorUnits(minor: number, currency: AccountCurrency): string {
  const amount = (Number.isFinite(minor) ? minor : 0) / 100;
  return new Intl.NumberFormat(currency === "gbp" ? "en-GB" : "en-US", {
    style: "currency",
    currency: currency === "gbp" ? "GBP" : "USD",
  }).format(amount);
}

export function formatCreditsAsMoney(credits: number, currency: AccountCurrency): string {
  return formatMinorUnits(minorUnitsForCredits(credits, minorPerHourForCurrency(currency)), currency);
}

/** A GBP catalog amount (pence) shown in the account currency. */
export function formatCatalogPence(pence: number, currency: AccountCurrency): string {
  return formatMinorUnits(convertMinorByOverageRate(pence, "gbp", currency), currency);
}

/** £/hour (or $/hour) represented by an internal per-minute credit rate. */
export function creditRatePerMinuteToHourlyMinor(creditsPerMinute: number, currency: AccountCurrency): number {
  if (!Number.isFinite(creditsPerMinute) || creditsPerMinute <= 0) return 0;
  return minorUnitsForCredits(creditsPerMinute * 60, minorPerHourForCurrency(currency));
}

export function hourlyMinorToCreditRatePerMinute(hourlyMinor: number, currency: AccountCurrency): number {
  const catalog = minorPerHourForCurrency(currency);
  if (!Number.isFinite(hourlyMinor) || hourlyMinor <= 0 || catalog <= 0) return 0;
  return (hourlyMinor * STANDARD_VOICE_CREDITS_PER_MINUTE) / catalog;
}

export type CatalogSku = "pay_as_you_go" | "pro" | "managed_campaign" | "small" | "medium" | "large";

export interface StripePriceShape {
  id?: string | null;
  unit_amount: number | null;
  currency: string | null;
  interval?: string | null;
}

export interface CatalogPriceOption {
  priceId: string;
  unitAmount: number;
  currency: "gbp" | "usd";
  interval: "month" | "hour" | null;
}

const ARCHIVED_PACKS = new Set<CatalogSku>(["small", "medium", "large"]);

export function catalogPriceOptions(sku: CatalogSku): CatalogPriceOption[] {
  const table = sku === "pro"
    ? STRIPE_CATALOG.pro
    : sku === "managed_campaign"
      ? STRIPE_CATALOG.managedCampaign
      : sku === "pay_as_you_go"
        ? STRIPE_CATALOG.payAsYouGo
        : null;
  if (!table) return [];
  return [
    { ...table.gbp, currency: "gbp" },
    { ...table.usd, currency: "usd" },
  ];
}

export function catalogPriceFor(sku: CatalogSku, currency: AccountCurrency): CatalogPriceOption | null {
  return catalogPriceOptions(sku).find((option) => option.currency === currency) ?? null;
}

export function creditsForVoiceDuration(durationSecs: number, creditsPerMinute: number): number {
  if (!Number.isFinite(durationSecs) || durationSecs <= 0) return 0;
  if (!Number.isFinite(creditsPerMinute) || creditsPerMinute <= 0) return 0;
  const secs = Math.round(durationSecs);
  if (secs <= 0) return 0;
  return Math.ceil((secs / 60) * creditsPerMinute);
}

/**
 * Minor units for a duration at an hourly price.
 * Same rounding as credits: round the seconds, then ceil the proportion.
 * Multiply before dividing so an exact number of minor units stays exact.
 */
export function minorUnitsForDuration(durationSecs: number, minorPerHour: number): number {
  if (!Number.isFinite(durationSecs) || durationSecs <= 0) return 0;
  if (!Number.isFinite(minorPerHour) || minorPerHour <= 0) return 0;
  const secs = Math.round(durationSecs);
  if (secs <= 0) return 0;
  return Math.ceil((secs * minorPerHour) / 3600);
}

export type OverageMarket = "UK" | "US";

/** Line country from the calling number, then the stored region. +44 is GBP, +1 is USD. */
export function overageMarketFromLine(phone?: string | null, region?: string | null): OverageMarket | null {
  let digits = (phone || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("44")) return "UK";
  if (digits.startsWith("1")) return "US";
  const code = (region || "").trim().toUpperCase();
  if (code === "UK" || code === "GB") return "UK";
  if (code === "US") return "US";
  return null;
}

/**
 * Seconds of the billed call that the credit shortfall represents.
 * A full shortfall (shortfall >= billable) keeps the rounded duration.
 */
export function overageSecondsForShortfall(input: {
  durationSecs: number;
  billableCredits: number;
  shortfallCredits: number;
}): number {
  const secs = Math.round(input.durationSecs);
  if (secs <= 0) return 0;
  const shortfall = input.shortfallCredits;
  const billable = input.billableCredits;
  if (!Number.isFinite(shortfall) || shortfall <= 0) return 0;
  if (!Number.isFinite(billable) || billable <= 0) return 0;
  if (shortfall >= billable) return secs;
  return Math.min(secs, Math.round(secs * (shortfall / billable)));
}

/** One billed minute at the standard voice rate. Held at dial and settled at hangup. */
export const CREDIT_HOLD_CREDITS = creditsForVoiceDuration(60, STANDARD_VOICE_CREDITS_PER_MINUTE);

/** Completed calls with a positive duration are billable. Everything else is zero. */
export function billableVoiceCredits(input: {
  status: string;
  durationSecs: number;
  creditsPerMinute: number;
}): number {
  if (input.status !== "completed") return 0;
  return creditsForVoiceDuration(input.durationSecs, input.creditsPerMinute);
}

/** Internal call-log figure. This is not the PayG hourly price and not a pack price. */
export function legacyAccountingGbp(credits: number): number {
  if (!Number.isFinite(credits) || credits <= 0) return 0;
  return credits / LEGACY_CREDITS_PER_POUND;
}

export interface VoiceSettlement {
  action: "skip" | "settle";
  billableCredits: number;
  reservedCredits: number;
  /** Positive deducts more, negative refunds unused hold. */
  delta: number;
  callCostGbp: number;
  creditsPerMinute: number;
  reason: string;
}

export function settleVoiceUsage(input: {
  status: string;
  durationSecs: number;
  creditsPerMinute: number;
  reservedCredits: number;
  alreadySettled: boolean;
}): VoiceSettlement {
  const reserved = Math.max(0, Math.round(Number(input.reservedCredits) || 0));
  if (input.alreadySettled) {
    return {
      action: "skip",
      billableCredits: 0,
      reservedCredits: reserved,
      delta: 0,
      callCostGbp: 0,
      creditsPerMinute: input.creditsPerMinute,
      reason: "already_settled",
    };
  }
  const billable = billableVoiceCredits(input);
  return {
    action: "settle",
    billableCredits: billable,
    reservedCredits: reserved,
    delta: billable - reserved,
    callCostGbp: legacyAccountingGbp(billable),
    creditsPerMinute: input.creditsPerMinute,
    reason: billable > 0 ? "completed_duration" : "non_billable",
  };
}

export function applyBalanceDelta(balance: number, delta: number): {
  nextBalance: number;
  appliedDelta: number;
  shortfall: number;
} {
  const current = Number.isFinite(balance) ? Math.max(0, balance) : 0;
  if (!Number.isFinite(delta) || delta === 0) {
    return { nextBalance: current, appliedDelta: 0, shortfall: 0 };
  }
  if (delta > 0) {
    const applied = Math.min(current, delta);
    return {
      nextBalance: current - applied,
      appliedDelta: applied,
      shortfall: delta - applied,
    };
  }
  return {
    nextBalance: current + (-delta),
    appliedDelta: delta,
    shortfall: 0,
  };
}

export function proIncludedMinor(currency: AccountCurrency): number {
  return currency === "usd" ? PRO_INCLUDED_CREDIT_CENTS : PRO_INCLUDED_CREDIT_PENCE;
}

/** Integer credits whose displayed value is not below the currency allowance. */
export function proIncludedCredits(currency: AccountCurrency): number {
  return creditsForMinorUnits(proIncludedMinor(currency), minorPerHourForCurrency(currency));
}

export function planPriceMinor(
  plan: "pro" | "pay_as_you_go" | "managed_campaign",
  currency: AccountCurrency,
): number {
  if (plan === "pro") return currency === "usd" ? PRO_PRICE_CENTS : PRO_PRICE_PENCE;
  if (plan === "managed_campaign") return currency === "usd" ? MANAGED_CAMPAIGN_CENTS : MANAGED_CAMPAIGN_PENCE;
  return currency === "usd" ? PAYG_CENTS_PER_HOUR : UK_OVERAGE_PENCE_PER_HOUR;
}

export function includedCreditsForTier(
  tier: string | null | undefined,
  currency: AccountCurrency = "gbp",
): number {
  const key = (tier || "").trim().toLowerCase();
  if (key === "pro") return proIncludedCredits(currency);
  if (key === "growth") return PRICE_TABLE.plans.growth.includedCredits;
  if (key === "starter") return PRICE_TABLE.plans.starter.includedCredits;
  return 0;
}

export function extraLinePencePerMonth(tier: string | null | undefined): number {
  const key = (tier || "free").trim().toLowerCase();
  if (key === "pro") return PRICE_TABLE.plans.pro.extraLinePencePerMonth;
  if (key === "growth") return PRICE_TABLE.plans.growth.extraLinePencePerMonth;
  return PRICE_TABLE.plans.payAsYouGo.extraLinePencePerMonth;
}

export interface InvoiceCreditDecision {
  kind: "refresh" | "leave_balance";
  included: number;
  rollover: number;
  nextCredits: number;
  reason: string;
}

/**
 * Subscription invoices refresh included credits for pro / starter / growth.
 * Pay as you go includes 0 credits, so an hourly invoice must not grant Pro
 * credits and must not wipe a purchased balance. Unknown tiers do the same.
 * Rollover stays capped at 50% of the included allowance. Purchased packs share
 * the same balance column, so a refresh can still drop credits above that cap.
 */
export function invoicePaidCredits(input: {
  tier: string | null | undefined;
  currentCredits: number;
  currency?: AccountCurrency | null;
}): InvoiceCreditDecision {
  const current = Number.isFinite(input.currentCredits) ? Math.max(0, input.currentCredits) : 0;
  const key = (input.tier || "").trim().toLowerCase();
  const refreshes = key === "pro" || key === "starter" || key === "growth";
  if (!refreshes) {
    return {
      kind: "leave_balance",
      included: 0,
      rollover: 0,
      nextCredits: current,
      reason: "tier has no included-credit refresh",
    };
  }
  const included = includedCreditsForTier(key, input.currency === "usd" ? "usd" : "gbp");
  const rolloverCap = Math.floor(included * ROLLOVER_CAP_RATIO);
  const rollover = Math.min(current, rolloverCap);
  return {
    kind: "refresh",
    included,
    rollover,
    nextCredits: included + rollover,
    reason: "subscription allowance refresh",
  };
}

export function creditsRequiredToStart(
  callCount: number,
  creditsPerMinute = STANDARD_VOICE_CREDITS_PER_MINUTE,
): number {
  const calls = Number.isFinite(callCount) ? Math.max(0, Math.floor(callCount)) : 0;
  return calls * creditsForVoiceDuration(60, creditsPerMinute);
}

export function canStartCreditCalls(
  balance: number,
  callCount = 1,
  creditsPerMinute = STANDARD_VOICE_CREDITS_PER_MINUTE,
): { ok: boolean; required: number } {
  const required = creditsRequiredToStart(callCount, creditsPerMinute);
  const current = Number.isFinite(balance) ? balance : 0;
  return { ok: current >= required && required > 0, required };
}

export function readReservedCredits(metadata: unknown): number {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return 0;
  const raw = (metadata as { credits_reserved?: unknown }).credits_reserved;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n);
}

export function isVoiceUsageSettled(log: {
  credits_used?: number | null;
  script_generation_metadata?: unknown;
}): boolean {
  const meta = log.script_generation_metadata;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    if ((meta as { billing_settled?: unknown }).billing_settled === true) return true;
  }
  return Number(log.credits_used ?? 0) > 0;
}

/** Null when the Stripe price matches one live per-currency catalog price. */
export function catalogPriceMismatch(sku: CatalogSku, actual: StripePriceShape): string | null {
  if (ARCHIVED_PACKS.has(sku)) {
    return `${sku} is an archived credit pack and is not available for checkout`;
  }
  const options = catalogPriceOptions(sku);
  const currency = (actual.currency || "").toLowerCase();
  const interval = actual.interval ?? null;
  const matched = options.some((option) => {
    const amountOk = actual.unit_amount === option.unitAmount;
    const currencyOk = currency === option.currency;
    const intervalOk = interval === option.interval;
    const idOk = !actual.id || actual.id === option.priceId;
    return amountOk && currencyOk && intervalOk && idOk;
  });
  if (matched) return null;
  const actualInterval = interval ?? "one_time";
  const accepted = options
    .map((option) => `${option.priceId} ${option.unitAmount} ${option.currency} ${option.interval}`)
    .join(" or ");
  return `${sku} Stripe price is ${actual.id ? `${actual.id} ` : ""}${actual.unit_amount ?? "null"} ${currency || "unknown"} ${actualInterval}; catalog accepts ${accepted}`;
}
